import { AxiosResponse } from "axios";
import { endPoints } from "utils/constants";
import { crudService } from "services/crud.service";
import { NotificationManager } from "react-notifications";
import { defaultMetaObj, initialTokenState } from "store/constants";
import { getErrorMessage, getPaginatedUrlWithSearchParams } from "store/helper";
import { PayloadAction, createSlice, createAsyncThunk } from "@reduxjs/toolkit";

const { tokenEndPoint } = endPoints;

export const fetchAllTokens = createAsyncThunk(
  "tokens/fetchAllTokens",
  async (
    {
      page,
      limit,
      searchParamsObj = {},
    }: { page: number; limit?: number; searchParamsObj?: {} },
    { rejectWithValue }
  ) => {
    const paginatedUrl = getPaginatedUrlWithSearchParams({
      pageNumber: page,
      limit,
      endpoint: tokenEndPoint,
      searchParams: {
        ...searchParamsObj,
      },
    });

    try {
      const response: AxiosResponse = await crudService.getAll(paginatedUrl);

      return response.data;
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

export const deleteToken = createAsyncThunk(
  "tokens/deleteToken",
  async ({ tokenId }: any, { rejectWithValue }) => {
    try {
      const response: any = await crudService.delete(tokenEndPoint, tokenId);

      if (response.status === 204) {
        NotificationManager.success("Token Deleted Successfully.");
        return response.data;
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

export const addNewToken = createAsyncThunk(
  "tokens/addNewToken",
  async (newToken: any, { rejectWithValue }) => {
    try {
      const response: AxiosResponse = await crudService.create(
        `${tokenEndPoint}/`,
        newToken
      );

      if (response.status === 201) {
        NotificationManager.success("Token Created Successfully.");
        return response.data;
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

export const updateToken = createAsyncThunk(
  "tokens/updateToken",
  async ({ updatedToken, tokenId }: any, { rejectWithValue }) => {
    try {
      const response: AxiosResponse = await crudService.update(
        tokenEndPoint,
        tokenId,
        updatedToken
      );
      if (response.status === 204) {
        NotificationManager.success("Token updated Successfully.");
        return response.data;
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

const tokenSlice = createSlice({
  name: "tokens",
  initialState: initialTokenState,
  reducers: {},
  extraReducers: (builder) => {
    // fetch all tasks
    builder
      .addCase(fetchAllTokens.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        fetchAllTokens.fulfilled,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.tokens = action.payload?.objects as any[];
          state.meta = action.payload?.meta;
        }
      )
      .addCase(
        fetchAllTokens.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
          state.tokens = [];
          state.meta = defaultMetaObj;
        }
      );

    // delete Task
    builder
      .addCase(deleteToken.pending, (State) => {
        State.loading = true;
      })
      .addCase(deleteToken.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
      })
      .addCase(
        deleteToken.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
        }
      );

    // add new task
    builder
      .addCase(addNewToken.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(addNewToken.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
      })
      .addCase(addNewToken.rejected, (state: any, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    //   update task
    builder
      .addCase(updateToken.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        updateToken.fulfilled,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
        }
      )
      .addCase(
        updateToken.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
        }
      );
  },
});

export const tokenReducer = tokenSlice.reducer;
